import React from 'react';
import {
  ActionDropDown, Badge, Dropdown, Portal, Skeleton,
  useModal,
} from '@doveit/bricks';
import {
  UpdateOfferStatusAction, computedOfferStatusAction, computedOfferStatusActionColors, computedOfferStatusActionIcon,
} from './constants';
import { useComputedOfferStatus } from '../../hooks/use-computed-offer-status/useComputedOfferStatus';
import { ComputedOfferStatus, OfferStatus } from '../../../domain/types';
import ArchiveOfferAction from '../archive-offer-action/ArchiveOfferAction';
import useOfferWizardData from '../../hooks/use-offer-wizard-data/useOfferWizardData';
import CancelOfferSignatureRequestAction from '../cancel-offer-signature-request-action/CancelOfferSignatureRequestAction';
import ConfirmOfferAction from '../confirm-offer-action/ConfirmOfferAction';
import { computedOfferStatusLabels, computedOfferStatusShortLabels } from '../../../labels';
import { Offer } from '../../../providers/api/dtos';
import EditOfferDeedAction from '../edit-offer-deed-action/EditOfferDeedAction';
import DeleteOfferSignatureRequestAction from '../delete-offer-signature-request-action/DeleteOfferSignatureRequestAction';
import useOfferSignatureStatus from '../../hooks/use-offer-signature-status/useOfferSignatureStatus';
import { isNotifiedOrAlreadyInitiated } from '../../../utils/digital-signature/signatureStatus';
import StartOfferAcceptanceSignatureRequestAction from '../start-offer-acceptance-signature-request-action/StartOfferAcceptanceSignatureRequestAction';
import CancelOfferAcceptanceSignatureRequestAction from '../cancel-offer-acceptance-signature-request-action/CancelOfferAcceptanceSignatureRequestAction';
import ProposeCounterOfferAction from '../propose-counter-offer-action/ProposeCounterOfferAction';
import MarketingOpportunitiesModal from '../../../marketing/components/marketing-opportunities-modal/MarketingOpportunitiesModal';
import { useAgentByPropertyId } from '../../../hooks/use-agent/useAgent';
import useIntent from '../../../intent/hooks/use-intent/useIntent';
import useUserPermissions from '../../../hooks/use-user-permissions/useUserPermissions';

export interface UpdateOfferStatusProps {
  intentId: string,
  lastOffer?: Offer,
  showAsBadge?: boolean,
  showShortLabel?: boolean,
  showIcon?: boolean,
  onSuccess?: VoidFunction,
  emphasis?: 'low' | 'high',
  size?: 'XS' | 'S',
}

const UpdateOfferStatus: React.FC<UpdateOfferStatusProps> = ({
  intentId,
  lastOffer,
  showAsBadge = false,
  showShortLabel = false,
  showIcon = false,
  onSuccess,
  emphasis = 'low',
  size = 'S',
}) => {
  const { data: offerWizardData } = useOfferWizardData(intentId);
  const {
    data: signatureStatus,
  } = useOfferSignatureStatus(offerWizardData?.signatureRequestId ? intentId : undefined, {
    refreshInterval: (data) => (data?.signers.every(isNotifiedOrAlreadyInitiated) ? 0 : 1000),
  });
  const userPermissions = useUserPermissions(['marketing']);
  const { data: offerStatus, isLoading: isComputedOfferStatusLoading } = useComputedOfferStatus(intentId, lastOffer);
  const { data: intent } = useIntent(intentId);
  const { data: agent } = useAgentByPropertyId(intent?.propertyId);
  const propertySoldMarketingModal = useModal();

  const canDoAction = React.useCallback(
    (action: UpdateOfferStatusAction) => offerStatus && computedOfferStatusAction(signatureStatus?.status, offerWizardData?.isDigitallySigned)[offerStatus.status as ComputedOfferStatus].includes(action),
    [offerStatus, offerWizardData?.isDigitallySigned, signatureStatus?.status],
  );

  const label = React.useMemo(() => {
    if (!offerStatus || offerStatus.status === 'NO_STATUS') {
      return '';
    }

    const labels = showShortLabel ? computedOfferStatusShortLabels : computedOfferStatusLabels;
    return labels[offerStatus.status];
  }, [offerStatus, showShortLabel]);

  const onConfirmOfferSuccess = React.useCallback(() => {
    if (userPermissions.marketing.canSeeSuggestions) {
      propertySoldMarketingModal.open();
    } else {
      onSuccess?.();
    }
  }, [onSuccess, propertySoldMarketingModal, userPermissions.marketing.canSeeSuggestions]);

  const marketingOpportunities = React.useMemo(() => {
    if (!intent?.propertyId || !agent?.id) return [];

    return [{
      type: 'property-sold' as const,
      propertyId: intent.propertyId,
      agentId: agent.id,
    }];
  }, [agent?.id, intent?.propertyId]);

  if (isComputedOfferStatusLoading) {
    return (
      <Skeleton width="7rem" />
    );
  }

  if (!offerStatus || offerStatus.status === 'NO_STATUS') {
    return null;
  }

  if (!lastOffer && !offerWizardData) {
    return null;
  }

  return (
    <>
      {(!offerStatus.canBeUpdated || showAsBadge)
        ? (
          <Badge
            label={label}
            color={computedOfferStatusActionColors[offerStatus.status]}
            icon={showIcon ? computedOfferStatusActionIcon[offerStatus.status] : undefined}
            emphasis={emphasis}
            size={size}
            aria-label="Badge per mostrare lo stato della proposta"
          />
        )
        : (
          <ActionDropDown
            label={label}
            color={computedOfferStatusActionColors[offerStatus.status]}
            iconLeft={showIcon ? { path: computedOfferStatusActionIcon[offerStatus.status] } : undefined}
            emphasis={emphasis}
            size={size}
            aria-label="Selezione per aggiornare lo stato della proposta"
          >
            {lastOffer && canDoAction(UpdateOfferStatusAction.CONFIRM_OFFER) && (
              <ConfirmOfferAction
                offer={lastOffer}
                onSuccess={onConfirmOfferSuccess}
              >
                {({ openConfirmOfferModal }) => (
                  <Dropdown.Option
                    label="Conferma acquisto"
                    onClick={openConfirmOfferModal}
                    aria-label="Azione per confermare l'acquisto di un immobile"
                  />
                )}
              </ConfirmOfferAction>
            )}

            {lastOffer && canDoAction(UpdateOfferStatusAction.ACCEPT_OFFER) && (
              <StartOfferAcceptanceSignatureRequestAction
                offer={lastOffer}
                onSuccess={onSuccess}
              >
                {({ ackSignatureRequest }) => (
                  <Dropdown.Option
                    label="Richiedi accettazione e presa visione"
                    onClick={ackSignatureRequest}
                    aria-label="Azione per richiedere accettazione e presa visione"
                  />
                )}
              </StartOfferAcceptanceSignatureRequestAction>
            )}

            {lastOffer && canDoAction(UpdateOfferStatusAction.PROPOSE_COUNTER_OFFER) && (
              <ProposeCounterOfferAction
                offer={lastOffer}
                onSuccess={onSuccess}
              >
                {({ proposeCounterOffer, isLoading }) => (
                  <Dropdown.Option
                    label="Invia controproposta"
                    onClick={proposeCounterOffer}
                    aria-label="Azione per inviare la richiesta di controproposta"
                    loading={isLoading}
                  />
                )}
              </ProposeCounterOfferAction>
            )}

            {lastOffer && canDoAction(UpdateOfferStatusAction.REFUSED_OFFER) && (
              <ArchiveOfferAction
                offer={lastOffer}
                toStatus={OfferStatus.REFUSED}
                aria-label="Azione per rifiutare una proposta"
                onSuccess={onSuccess}
              >
                {({ updateStatus }) => (
                  <Dropdown.Option
                    label="Rifiuta proposta"
                    onClick={updateStatus}
                  />
                )}
              </ArchiveOfferAction>
            )}

            {lastOffer && canDoAction(UpdateOfferStatusAction.WITHDRAW_OFFER) && (
              <ArchiveOfferAction
                offer={lastOffer}
                toStatus={OfferStatus.WITHDRAWN}
                aria-label="Azione per annullare una proposta"
                onSuccess={onSuccess}
              >
                {({ updateStatus }) => (
                  <Dropdown.Option
                    label="Annulla proposta"
                    onClick={updateStatus}
                  />
                )}
              </ArchiveOfferAction>
            )}

            {lastOffer && canDoAction(UpdateOfferStatusAction.ARCHIVE_FOR_MORTGAGE) && (
              <ArchiveOfferAction
                offer={lastOffer}
                toStatus={OfferStatus.MORTGAGE_REFUSED}
                aria-label="Azione per archiviare una proposta causa mutuo non concesso"
                onSuccess={onSuccess}
              >
                {({ updateStatus }) => (
                  <Dropdown.Option
                    label="Archivia causa mutuo"
                    onClick={updateStatus}
                  />
                )}
              </ArchiveOfferAction>
            )}

            {lastOffer && canDoAction(UpdateOfferStatusAction.ARCHIVE_FOR_CONSTRAINT) && (
              <ArchiveOfferAction
                offer={lastOffer}
                toStatus={OfferStatus.OTHER_CONSTRAINTS}
                aria-label="Azione per archiviare una proposta causa altri vincoli"
                onSuccess={onSuccess}
              >
                {({ updateStatus }) => (
                  <Dropdown.Option
                    label="Archivia causa altri vincoli"
                    onClick={updateStatus}
                  />
                )}
              </ArchiveOfferAction>
            )}

            {offerWizardData && canDoAction(UpdateOfferStatusAction.CANCEL_REQUEST) && (
              <CancelOfferSignatureRequestAction
                offerWizardData={offerWizardData}
                aria-label="Azione per annullare la richiesta di firma della proposta digitale"
                onSuccess={onSuccess}
              >
                {({ cancelSignatureRequest }) => (
                  <Dropdown.Option
                    label="Annulla richiesta"
                    onClick={cancelSignatureRequest}
                  />
                )}
              </CancelOfferSignatureRequestAction>
            )}

            {lastOffer && canDoAction(UpdateOfferStatusAction.CANCEL_ACCEPTANCE_REQUEST) && (
              <CancelOfferAcceptanceSignatureRequestAction
                offer={lastOffer}
                aria-label="Azione per annullare la richiesta di firma della presa visione e accettazione della proposta digitale"
                onSuccess={onSuccess}
              >
                {({ cancelSignatureRequest }) => (
                  <Dropdown.Option
                    label="Annulla richiesta"
                    onClick={cancelSignatureRequest}
                  />
                )}
              </CancelOfferAcceptanceSignatureRequestAction>
            )}

            {offerWizardData && canDoAction(UpdateOfferStatusAction.DELETE_REQUEST) && (
              <DeleteOfferSignatureRequestAction
                offerWizardData={offerWizardData}
                aria-label="Azione per cancellare la richiesta di firma della proposta digitale"
                onSuccess={onSuccess}
              >
                {({ deleteSignatureRequest }) => (
                  <Dropdown.Option
                    label="Cancella richiesta"
                    onClick={deleteSignatureRequest}
                  />
                )}
              </DeleteOfferSignatureRequestAction>
            )}

            {lastOffer && canDoAction(UpdateOfferStatusAction.CONFIRM_DEED) && (
              <EditOfferDeedAction
                offer={lastOffer}
                onSuccess={onSuccess}
                isDone
              >
                {({ openEditOfferDeedModal }) => (
                  <Dropdown.Option
                    label="Conferma rogito"
                    aria-label="Azione per confermare il rogito"
                    onClick={openEditOfferDeedModal}
                  />
                )}
              </EditOfferDeedAction>
            )}
          </ActionDropDown>
        )}
      {userPermissions.marketing.canSeeSuggestions && (
        <Portal>
          <MarketingOpportunitiesModal
            triggers={marketingOpportunities}
            {...propertySoldMarketingModal}
            close={() => {
              propertySoldMarketingModal.close();
              onSuccess?.();
            }}
          />
        </Portal>
      )}
    </>
  );
};

export default UpdateOfferStatus;
